import React from "react";
import Text from "@/components/ui/text";
import Input from "@/components/ui/input";
import { InfoCircle } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";

interface Props {
  /** Hint line rendered above the card, e.g. "Start with a company domain…". */
  hint: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  /** Rendered below the field in error styling; also puts the input in its error state. */
  error?: string | null;
  /** Non-blocking info line below the field, e.g. the personal-email advisory. Error wins when both are set. */
  notice?: string | null;
  dataTest?: string;
  /** Extra content under the field — match rows, advisory boxes. */
  children?: React.ReactNode;
}

/** The blue-tinted single-field card shared by every "With Dreamer" create tab. */
const DreamerFieldCard: React.FC<Props> = ({
  hint,
  label,
  placeholder,
  value,
  onChange,
  error,
  notice,
  dataTest,
  children
}) => {
  const inputId = React.useId();
  const errorId = `${inputId}-error`;
  const noticeId = `${inputId}-notice`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 opacity-80">
        <Text variant="bodySecondary" className="px-1">
          {hint}
        </Text>
      </div>

      <div className="flex flex-col gap-1 rounded-2xl border border-border-dreamer-card bg-surface-dreamer-card px-4 pb-6 pt-4">
        <div className="flex min-h-6 items-center gap-1 pl-1">
          <Text as="label" htmlFor={inputId} variant="bodySecondary">
            {label}
          </Text>
          {/* aria-hidden: the input's own `required` conveys this to AT. */}
          <Text variant="body" aria-hidden="true">
            *
          </Text>
        </div>

        <Input
          id={inputId}
          required
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          hasError={!!error}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : notice ? noticeId : undefined}
          data-test={dataTest}
        />

        {error ? (
          <Text
            id={errorId}
            variant="smBodySecondary"
            className="pl-1 text-error"
            data-test={dataTest ? `${dataTest}-error` : undefined}
          >
            {error}
          </Text>
        ) : notice ? (
          <div
            id={noticeId}
            className="flex items-center gap-1 pl-1"
            data-test={dataTest ? `${dataTest}-notice` : undefined}
          >
            <IconContainer
              as="span"
              color="secondary"
              nativeStroke
              className="shrink-0"
            >
              <InfoCircle />
            </IconContainer>
            <Text variant="smBodySecondary">{notice}</Text>
          </div>
        ) : null}

        {children}
      </div>
    </div>
  );
};

export default DreamerFieldCard;
